<script setup>
import { ref, watch, nextTick, toRef } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useSearchStore } from '@js/store/SearchStore.js'
import { useSwipePanel } from '@js/useSwipePanel.js'
import IconX from '@js/components/Icons/IconX.vue'
import ImageLoader from '@js/components/Loaders/ImageLoader.vue'
import NumberFormatComponent from '@js/components/Formatter/NumberFormatComponent.vue'
import SearchDesktopLoadingSkeleton from '@js/components/Search/SearchDesktopLoadingSkeleton.vue'
import IconSearch from '@js/components/Icons/IconSearch.vue'

const searchStore = useSearchStore()
const searchInput = ref(null)
const panelRef = ref(null)
const dragHandleRef = ref(null)

const {
    rendered,
    panelStyle,
    backdropStyle,
    close,
    handleBackgroundClick,
} = useSwipePanel({
    isOpen: toRef(searchStore, 'show'),
    panelRef,
    handleRef: dragHandleRef,
    direction: 'bottom',
    closeThreshold: 110,
    openDuration: 300,
    closeDuration: 280,
    fadeDivisor: 240,
    lockScroll: true,
    onClose: () => {
        searchStore.show = false
    },
})

const getProductImage = (result) => {
    const firstImage = Array.isArray(result?.images) ? result.images[0] : null
    return firstImage ? `/storage/products/${firstImage}` : '/images/no-image.webp'
}

const categoryLine = (result) => {
    const parts = []

    if (result?.category?.title) {
        parts.push(result.category.title)
    }

    if (result?.sub_category?.title) {
        parts.push(result.sub_category.title)
    }

    return parts.join(' • ')
}

watch(
    () => searchStore.query,
    () => {
        searchStore.getSearchData()
    }
)

router.on('before', () => {
    searchStore.show = false
    searchStore.query = ''
})

watch(
    () => searchStore.show,
    async (shown) => {
        if (shown) {
            await nextTick()
            searchInput.value?.focus()
        }
    }
)

const closeSearch = () => {
    close()
}

const onResultClick = () => {
    close()
}
</script>

<template>
    <div
        v-if="rendered"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        @click="handleBackgroundClick"
    >
        <div
            class="absolute inset-0 bg-black/50"
            :style="backdropStyle"
        ></div>

        <div
            ref="panelRef"
            :style="panelStyle"
            class="relative z-[1] w-full bg-white rounded-t-2xl sm:rounded-2xl shadow-lg overflow-hidden sm:max-w-[900px] h-[95vh] sm:max-h-[900px]"
            @click.stop
        >
            <div class="absolute top-3 right-0 left-0 m-auto z-10 flex justify-center">
                <div
                    ref="dragHandleRef"
                    class="h-8 w-24 flex items-start justify-center select-none"
                    style="touch-action: none; user-select: none;"
                >
                    <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
                </div>
            </div>

            <div class="flex h-full flex-col pt-5 sm:pt-8">
                <div class="flex items-center p-4 border-b border-gray-200 shrink-0">
                    <div class="relative w-full">
                        <input
                            type="search"
                            id="location-search"
                            ref="searchInput"
                            v-model="searchStore.query"
                            @click.prevent="searchStore.showDropdown"
                            class="block p-2.5 w-full z-20 text-gray-900 rounded border-baseColor border-2 focus:ring-0 focus:border-baseColor"
                            :placeholder="searchStore.currentPlaceholder"
                        />

                        <button
                            v-if="searchStore.query"
                            @click.prevent="searchStore.clearQuery"
                            type="button"
                            class="absolute inset-y-0 end-[50px] flex items-center px-2 text-gray-400"
                            aria-label="Очистить поиск"
                        >
                            <IconX class="w-5 h-5" />
                        </button>

                        <Link
                            type="submit"
                            :href="route('comingSoon', { search: searchStore.query })"
                            @click.prevent="searchStore.initSearch"
                            class="flex items-center justify-center absolute top-0 end-0 h-full p-2.5 text-sm font-medium text-black bg-baseColor rounded-e border border-baseColor md:hover:bg-baseColor/80 focus:outline-none focus:ring-0 transition"
                        >
                            <IconSearch class="w-5 h-5" />
                        </Link>
                    </div>
                </div>

                <div class="flex-1 overflow-y-auto">
                    <template v-if="searchStore.resultsLoaded">
                        <div v-if="searchStore.results.length">
                            <Link
                                v-for="result in searchStore.results"
                                :key="result.id"
                                :href="route('product', result.slug)"
                                @click="onResultClick"
                                class="flex items-center gap-4 px-4 py-3 border-b md:hover:bg-gray-50 cursor-pointer transition"
                            >
                                <ImageLoader
                                    :src="getProductImage(result)"
                                    :title="result.title"
                                    class="min-w-[70px] min-h-[70px] rounded-md border"
                                    imgClasses="w-[70px] h-[70px] object-contain"
                                />

                                <div class="flex min-w-0 flex-col gap-1.5">
                                    <div class="text-sm font-medium line-clamp-2">
                                        {{ result.title }}
                                    </div>

                                    <div
                                        v-if="categoryLine(result)"
                                        class="text-[12px] leading-4 text-gray-500 line-clamp-1"
                                    >
                                        {{ categoryLine(result) }}
                                    </div>

                                    <div class="text-sm font-semibold text-yellow-600">
                                        <NumberFormatComponent
                                            v-if="result.price"
                                            :number="result.price"
                                            append_text=" ₽"
                                        />
                                        <div v-else>
                                            Договорная
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>

                        <div v-else class="px-4 py-3 text-sm text-gray-500">
                            {{ searchStore.query ? 'Ничего не найдено' : 'Введите для поиска' }}
                        </div>
                    </template>

                    <template v-else>
                        <SearchDesktopLoadingSkeleton v-for="i in 4" :key="i" />
                    </template>

                    <div
                        v-if="searchStore.history.length"
                        class="p-3 border-t text-sm text-gray-400"
                    >
                        История
                    </div>

                    <ul v-if="searchStore.history.length" class="mb-2">
                        <li
                            v-for="item in searchStore.history"
                            :key="item"
                            class="px-4 py-2 text-sm text-gray-700 md:hover:bg-grayBg flex justify-between items-center"
                        >
                            <span
                                @click.prevent="searchStore.repeatHistory(item)"
                                class="cursor-pointer w-full"
                            >
                                {{ item }}
                            </span>

                            <button
                                @click.stop.prevent="searchStore.removeFromHistory(item)"
                                class="group ml-2 text-gray-400 transition text-xs"
                            >
                                <IconX class="w-4 h-4 group-hover:stroke-red-600 transition" />
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.line-clamp-1 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 1;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
